import { useState, useEffect, useRef } from 'react';
import { Atendimento, AtendimentoFormData, ApiResponse } from '../types';
import { 
  createAtendimento, 
  getAtendimentos, 
  getAtendimentosDoDia,
  finalizarAtendimento,
  cancelarAtendimento,
  updateAtendimento,
  deleteAtendimento,
  subscribeToAtendimentosDoDia 
} from '../services/atendimentoService';
import { toast } from 'react-toastify';

export const useAtendimentos = (userId: string) => {
  const [atendimentos, setAtendimentos] = useState<Atendimento[]>([]);
  const [atendimentosDoDia, setAtendimentosDoDia] = useState<Atendimento[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  
  // 🔥 ADICIONAR: Referência para armazenar a função de desinscrever
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const loadAtendimentos = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getAtendimentos(userId);
      
      if (result.success && result.data) {
        setAtendimentos(result.data);
      } else {
        setError(result.error || 'Erro ao carregar atendimentos');
        toast.error(result.error || 'Erro ao carregar atendimentos');
      }
    } catch (err) {
      const errorMessage = 'Erro ao carregar atendimentos';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 MODIFICAR: Esta função agora não é mais necessária ser chamada manualmente
  // mas mantém compatibilidade com código existente
  const loadAtendimentosDoDia = async (data?: string) => {
    try {
      setError('');
      const result = await getAtendimentosDoDia(userId, data);
      
      if (result.success && result.data) {
        setAtendimentosDoDia(result.data);
      } else {
        setError(result.error || 'Erro ao carregar atendimentos do dia');
        toast.error(result.error || 'Erro ao carregar atendimentos do dia');
      }
    } catch (err) {
      const errorMessage = 'Erro ao carregar atendimentos do dia';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const addAtendimento = async (
    atendimentoData: AtendimentoFormData,
    funcionarioNome: string
  ): Promise<ApiResponse<Atendimento>> => {
    try {
      setError('');
      const result = await createAtendimento(atendimentoData, funcionarioNome, userId);
      
      if (result.success && result.data) {
        setAtendimentos(prev => [result.data!, ...prev]);
        // 👈 NÃO precisa mais atualizar manualmente - o listener fará isso
        toast.success('Atendimento iniciado com sucesso!');
        return { success: true, data: result.data };
      } else {
        const errorMessage = result.error || 'Erro ao iniciar atendimento';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao iniciar atendimento';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const finalizar = async (
    id: string, 
    resultado: string, 
    observacoes?: string,
    reservaId?: string
  ) => {
    try {
      setError('');
      const result = await finalizarAtendimento(id, resultado, observacoes, reservaId);
      
      if (result.success) {
        // 👈 NÃO precisa mais recarregar - o listener atualizará automaticamente
        toast.success('Atendimento finalizado com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao finalizar atendimento';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao finalizar atendimento';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const cancelar = async (id: string, motivo?: string) => {
    try {
      setError('');
      const result = await cancelarAtendimento(id, motivo);
      
      if (result.success) {
        // 👈 NÃO precisa mais atualizar manualmente - o listener fará isso
        toast.success('Atendimento cancelado');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao cancelar atendimento';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao cancelar atendimento';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const atualizar = async (id: string, updates: Partial<Atendimento>) => {
    try {
      setError('');
      const result = await updateAtendimento(id, updates);
      
      if (result.success) {
        // 👈 NÃO precisa mais atualizar manualmente - o listener fará isso
        toast.success('Atendimento atualizado com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao atualizar atendimento';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao atualizar atendimento';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const remover = async (id: string) => {
    try {
      setError('');
      const result = await deleteAtendimento(id);
      
      if (result.success) {
        // 👈 NÃO precisa mais atualizar manualmente - o listener fará isso
        toast.success('Atendimento removido com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao remover atendimento';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao remover atendimento';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 🔥 MODIFICAR O useEffect PARA USAR O LISTENER
  useEffect(() => {
    if (!userId) return;

    // Carregar atendimentos gerais (sem listener)
    loadAtendimentos();

    // 🔥 Criar listener em tempo real para atendimentos do dia
    const dataHoje = new Date().toISOString().split('T')[0];
    
    setLoading(true);
    
    const unsubscribe = subscribeToAtendimentosDoDia(
      userId,
      dataHoje,
      (atendimentosAtualizados) => {
        setAtendimentosDoDia(atendimentosAtualizados);
        setLoading(false);
      },
      (error) => {
        console.error('Erro no listener:', error);
        setError('Erro ao sincronizar atendimentos em tempo real');
        setLoading(false);
      }
    );

    // Armazenar função de desinscrever
    unsubscribeRef.current = unsubscribe;

    // 🧹 Cleanup: Desinscrever quando o componente for desmontado
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [userId]);

  return {
    atendimentos,
    atendimentosDoDia,
    loading,
    error,
    loadAtendimentos,
    loadAtendimentosDoDia,
    addAtendimento,
    finalizar,
    cancelar,
    atualizar,
    remover
  };
};

